import type {
	AutomaticSourcesMode,
	PreviousStepDocumentsMode,
} from '@Application/Organizations/Workflows/Actions/organizationWorkflowsFetched';
import type { SaveWorkflowStep } from '@Application/Organizations/Workflows/Commands/SaveOrganizationWorkflow';
import isDocumentTypeSelected from '@UI/Pages/Administration/DocumentGroups/DocumentGroupEdit/FormData/constraint/isDocumentTypeSelected';
import isTemperatureValid from '@UI/Pages/Administration/DocumentGroups/DocumentGroupEdit/FormData/constraint/isTemperatureValid';
import type DocumentTypePresetView from '@UI/Store/OrganizationWorkflows/Views/DocumentTypePresetView';
import type DocumentTypeSectionView from '@UI/Store/OrganizationWorkflows/Views/DocumentTypeSectionView';
import type WorkflowStepView from '@UI/Store/OrganizationWorkflows/Views/WorkflowStepView';
import { AbstractValidatedClass, Joi, ValidatedClass, constraint } from '@verclam/form-validator';

type SectionDependencies = Readonly<Record<string, readonly string[]>>;

type DocumentSettingsArgs = {
	documentTypeId: number | null;
	/** Empty means the label of the document type. */
	label: string;
	/** Null means the catalogue default. */
	systemPrompt: string | null;
	contextPrompt: string | null;
	preparationElements: boolean;
	previousStepDocumentsMode: PreviousStepDocumentsMode;
	previousStepDocumentTypeIds: readonly number[];
	automaticSourcesMode: AutomaticSourcesMode;
	automaticSourceDocumentTypeIds: readonly number[];
	reevaluationEnabled: boolean;
	reevaluationSystemPrompt: string | null;
	reevaluationContextPrompt: string | null;
	splitCalls: boolean;
	sectionDependencies: SectionDependencies;
	/** Null inherits the value configured for the agency, the organization or the account. */
	temperature: number | null;
	/** One term per line, kept raw so that typing a line break is never swallowed. */
	vocabularyText: string;
};

const freezeDependencies = (dependencies: SectionDependencies): SectionDependencies => {
	const copy: Record<string, readonly string[]> = {};

	for (const [path, targets] of Object.entries(dependencies)) {
		copy[path] = Object.freeze([...targets]);
	}

	return Object.freeze(copy);
};

/**
 * Immutable form state of one document of a group: its type, label, prompts, inputs, splitting and
 * advanced settings. The type is fixed once the document is added; everything else is tuned in the
 * document settings screen and applied back to the group draft as a whole.
 */
@ValidatedClass()
class DocumentSettingsFormData extends AbstractValidatedClass<DocumentSettingsArgs> {
	@constraint(isDocumentTypeSelected, () => 'Le type de document est requis.')
	public readonly documentTypeId: number | null;

	@constraint(Joi.string().max(180).allow(''), () => 'Le libellé ne peut pas dépasser 180 caractères.')
	public readonly label: string;

	public readonly systemPrompt: string | null;
	public readonly contextPrompt: string | null;
	public readonly preparationElements: boolean;
	public readonly previousStepDocumentsMode: PreviousStepDocumentsMode;
	public readonly previousStepDocumentTypeIds: readonly number[];
	public readonly automaticSourcesMode: AutomaticSourcesMode;
	public readonly automaticSourceDocumentTypeIds: readonly number[];
	public readonly reevaluationEnabled: boolean;
	public readonly reevaluationSystemPrompt: string | null;
	public readonly reevaluationContextPrompt: string | null;
	public readonly splitCalls: boolean;
	public readonly sectionDependencies: SectionDependencies;

	@constraint(isTemperatureValid, () => 'La température doit être comprise entre 0 et 2.')
	public readonly temperature: number | null;

	public readonly vocabularyText: string;

	constructor(data: DocumentSettingsArgs) {
		super(data);
		this.documentTypeId = data.documentTypeId;
		this.label = data.label;
		this.systemPrompt = data.systemPrompt;
		this.contextPrompt = data.contextPrompt;
		this.preparationElements = data.preparationElements;
		this.previousStepDocumentsMode = data.previousStepDocumentsMode;
		this.previousStepDocumentTypeIds = Object.freeze([...data.previousStepDocumentTypeIds]);
		this.automaticSourcesMode = data.automaticSourcesMode;
		this.automaticSourceDocumentTypeIds = Object.freeze([...data.automaticSourceDocumentTypeIds]);
		this.reevaluationEnabled = data.reevaluationEnabled;
		this.reevaluationSystemPrompt = data.reevaluationSystemPrompt;
		this.reevaluationContextPrompt = data.reevaluationContextPrompt;
		this.splitCalls = data.splitCalls;
		this.sectionDependencies = freezeDependencies(data.sectionDependencies);
		this.temperature = data.temperature;
		this.vocabularyText = data.vocabularyText;
		Object.freeze(this);
	}

	/**
	 * A freshly added document starts from the preset of its type; without a catalogue the document
	 * starts neutral and every prompt follows the catalogue default.
	 */
	static createForDocumentType(
		documentTypeId: number,
		preset: DocumentTypePresetView | null,
	): DocumentSettingsFormData {
		const splitCalls = preset?.splitCalls ?? false;

		return new DocumentSettingsFormData({
			documentTypeId,
			label: '',
			systemPrompt: null,
			contextPrompt: null,
			preparationElements: true,
			previousStepDocumentsMode: 'all',
			previousStepDocumentTypeIds: [],
			automaticSourcesMode: 'all',
			automaticSourceDocumentTypeIds: [],
			reevaluationEnabled: false,
			reevaluationSystemPrompt: null,
			reevaluationContextPrompt: null,
			splitCalls,
			sectionDependencies: splitCalls ? (preset?.sectionDependencies ?? {}) : {},
			temperature: null,
			vocabularyText: '',
		});
	}

	static fromStep(step: WorkflowStepView): DocumentSettingsFormData {
		const prompts = step.prompts;
		const reevaluation = step.reevaluation;
		const inputs = step.inputs;

		return new DocumentSettingsFormData({
			documentTypeId: step.documentTypeId,
			label: step.label,
			systemPrompt: prompts?.isSystemPromptPersonalized === true ? prompts.systemPrompt : null,
			contextPrompt: prompts?.isContextPromptPersonalized === true ? prompts.contextPrompt : null,
			preparationElements: inputs?.preparationElements ?? true,
			previousStepDocumentsMode: inputs?.previousStepDocumentsMode ?? 'all',
			previousStepDocumentTypeIds: inputs?.previousStepDocumentTypeIds ?? [],
			automaticSourcesMode: inputs?.automaticSourcesMode ?? 'all',
			automaticSourceDocumentTypeIds: inputs?.automaticSourceDocumentTypeIds ?? [],
			reevaluationEnabled: reevaluation?.enabled ?? false,
			reevaluationSystemPrompt: reevaluation?.prompts.isSystemPromptPersonalized
				? reevaluation.prompts.systemPrompt
				: null,
			reevaluationContextPrompt: reevaluation?.prompts.isContextPromptPersonalized
				? reevaluation.prompts.contextPrompt
				: null,
			splitCalls: step.splitCalls,
			sectionDependencies: step.sectionDependencies,
			temperature: step.temperature,
			vocabularyText: (step.vocabularyTerms ?? []).join('\n'),
		});
	}

	get vocabularyTerms(): readonly string[] {
		return this.vocabularyText
			.split('\n')
			.map((term) => term.trim())
			.filter((term) => term !== '');
	}

	get hasPersonalizedPrompts(): boolean {
		return this.systemPrompt !== null || this.contextPrompt !== null;
	}

	setLabel(label: string): this {
		return this.cloneWith({ label } as Partial<DocumentSettingsArgs>);
	}

	setSystemPrompt(systemPrompt: string | null): this {
		return this.cloneWith({ systemPrompt } as Partial<DocumentSettingsArgs>);
	}

	setContextPrompt(contextPrompt: string | null): this {
		return this.cloneWith({ contextPrompt } as Partial<DocumentSettingsArgs>);
	}

	setPreparationElements(preparationElements: boolean): this {
		return this.cloneWith({ preparationElements } as Partial<DocumentSettingsArgs>);
	}

	setPreviousStepDocumentsMode(previousStepDocumentsMode: PreviousStepDocumentsMode): this {
		return this.cloneWith({ previousStepDocumentsMode } as Partial<DocumentSettingsArgs>);
	}

	setPreviousStepDocumentTypeIds(previousStepDocumentTypeIds: readonly number[]): this {
		return this.cloneWith({ previousStepDocumentTypeIds } as Partial<DocumentSettingsArgs>);
	}

	setAutomaticSourcesMode(automaticSourcesMode: AutomaticSourcesMode): this {
		return this.cloneWith({ automaticSourcesMode } as Partial<DocumentSettingsArgs>);
	}

	setAutomaticSourceDocumentTypeIds(automaticSourceDocumentTypeIds: readonly number[]): this {
		return this.cloneWith({ automaticSourceDocumentTypeIds } as Partial<DocumentSettingsArgs>);
	}

	setReevaluationEnabled(reevaluationEnabled: boolean): this {
		return this.cloneWith({ reevaluationEnabled } as Partial<DocumentSettingsArgs>);
	}

	setReevaluationSystemPrompt(reevaluationSystemPrompt: string | null): this {
		return this.cloneWith({ reevaluationSystemPrompt } as Partial<DocumentSettingsArgs>);
	}

	setReevaluationContextPrompt(reevaluationContextPrompt: string | null): this {
		return this.cloneWith({ reevaluationContextPrompt } as Partial<DocumentSettingsArgs>);
	}

	setSplitCalls(splitCalls: boolean): this {
		return this.cloneWith({ splitCalls } as Partial<DocumentSettingsArgs>);
	}

	/** Sets the sections (or fields) a section waits for; an empty list removes the dependency. */
	setSectionDependency(sectionPath: string, targets: readonly string[]): this {
		const { [sectionPath]: _removed, ...others } = this.sectionDependencies;
		const sectionDependencies = targets.length > 0 ? { ...others, [sectionPath]: targets } : others;

		return this.cloneWith({ sectionDependencies } as Partial<DocumentSettingsArgs>);
	}

	/** Number of (section, waited-for element) pairs; the difference before/after a cleanup is the removed count. */
	get sectionDependencyCount(): number {
		return Object.values(this.sectionDependencies).reduce((total, targets) => total + targets.length, 0);
	}

	/**
	 * Keeps only the dependencies the template still offers: the waiting section must be a section of
	 * the template, and each element it waits for a section or a field of it. A section left with no
	 * element is dropped.
	 */
	retainKnownSectionDependencies(sections: readonly DocumentTypeSectionView[]): this {
		const sectionPaths = new Set(sections.map((section) => section.path));
		const knownPaths = new Set(
			sections.flatMap((section) => [section.path, ...section.fields.map((field) => field.path)]),
		);
		const sectionDependencies: Record<string, readonly string[]> = {};

		for (const [sectionPath, targets] of Object.entries(this.sectionDependencies)) {
			const retained = targets.filter((target) => knownPaths.has(target));

			if (sectionPaths.has(sectionPath) && retained.length > 0) {
				sectionDependencies[sectionPath] = retained;
			}
		}

		return this.cloneWith({ sectionDependencies } as Partial<DocumentSettingsArgs>);
	}

	setTemperature(temperature: number | null): this {
		return this.cloneWith({ temperature } as Partial<DocumentSettingsArgs>);
	}

	setVocabularyText(vocabularyText: string): this {
		return this.cloneWith({ vocabularyText } as Partial<DocumentSettingsArgs>);
	}

	/** Drops the references to documents the group no longer provides as input. */
	withInputsLimitedTo(
		previousDocumentTypeIds: readonly number[],
		automaticDocumentTypeIds: readonly number[],
	): this {
		return this.cloneWith({
			previousStepDocumentTypeIds: this.previousStepDocumentTypeIds.filter((id) =>
				previousDocumentTypeIds.includes(id),
			),
			automaticSourceDocumentTypeIds: this.automaticSourceDocumentTypeIds.filter((id) =>
				automaticDocumentTypeIds.includes(id),
			),
		} as Partial<DocumentSettingsArgs>);
	}

	/**
	 * The save payload of this document. `hasPreviousStages` and `hasAutomaticSources` tell which
	 * inputs exist at all in the group: absent ones are sent neutral rather than as a dangling choice.
	 */
	toDraftStep(
		documentTypeLabel: string,
		hasPreviousStages: boolean,
		hasAutomaticSources: boolean,
	): SaveWorkflowStep {
		if (this.documentTypeId === null) {
			throw new Error('A document needs a type before it can be saved.');
		}

		const trimmedLabel = this.label.trim();
		const previousMode: PreviousStepDocumentsMode = hasPreviousStages
			? this.previousStepDocumentsMode
			: 'none';
		const automaticMode: AutomaticSourcesMode = hasAutomaticSources ? this.automaticSourcesMode : 'all';
		const dependencies = Object.fromEntries(
			Object.entries(this.sectionDependencies).filter(([, targets]) => targets.length > 0),
		);
		const vocabularyTerms = this.vocabularyTerms;

		return {
			documentTypeId: this.documentTypeId,
			label: trimmedLabel === '' || trimmedLabel === documentTypeLabel ? null : trimmedLabel,
			systemPrompt: this.systemPrompt,
			contextPrompt: this.contextPrompt,
			inputs: {
				preparationElements: this.preparationElements,
				previousStepDocumentsMode: previousMode,
				previousStepDocumentTypeIds:
					previousMode === 'selected' ? this.previousStepDocumentTypeIds : [],
				automaticSourcesMode: automaticMode,
				automaticSourceDocumentTypeIds:
					automaticMode === 'selected' ? this.automaticSourceDocumentTypeIds : [],
			},
			reevaluation: {
				enabled: this.reevaluationEnabled,
				systemPrompt: this.reevaluationEnabled ? this.reevaluationSystemPrompt : null,
				contextPrompt: this.reevaluationEnabled ? this.reevaluationContextPrompt : null,
			},
			splitCalls: this.splitCalls,
			sectionDependencies: this.splitCalls ? dependencies : null,
			temperature: this.temperature,
			vocabularyTerms: vocabularyTerms.length > 0 ? vocabularyTerms : null,
		};
	}
}

export default DocumentSettingsFormData;
